import { useId, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import Select, { createFilter } from 'react-select';
import { useMediaQuery } from 'react-responsive';

const defaultGetOptionValue = (option) => option.value;
const defaultGetOptionLabel = (option) => option.label;
const reactSelectFilter = createFilter();

const ResponsiveMultiSelect = ({
    label,
    options = [],
    value = [],
    onChange,
    placeholder = 'Select...',
    searchPlaceholder,
    isDisabled = false,
    getOptionValue = defaultGetOptionValue,
    getOptionLabel = defaultGetOptionLabel,
    isOptionDisabled,
    formatOptionLabel,
    mobileFormatOptionLabel,
    className = 'react-select',
    classNamePrefix = 'react-select',
    inputId,
    name,
    ...selectProps
}) => {
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const generatedId = useId().replace(/:/g, '');
    const triggerId = inputId || `${name || 'multi-select'}-${generatedId}`;
    const titleId = `${triggerId}-sheet-title`;
    const triggerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [draftValue, setDraftValue] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    const groups = useMemo(() => options.map((item) => (
        Array.isArray(item.options)
            ? { label: item.label, options: item.options }
            : { label: null, options: [item] }
    )), [options]);

    const optionKey = (option) => String(getOptionValue(option));
    const draftKeys = new Set(draftValue.map(optionKey));

    const filteredGroups = useMemo(() => {
        const query = searchValue.trim();
        if (!query) return groups;

        return groups
            .map((group) => ({
                ...group,
                options: group.options.filter((option) => reactSelectFilter({
                    label: String(getOptionLabel(option)),
                    value: String(getOptionValue(option)),
                    data: option,
                }, query)),
            }))
            .filter((group) => group.options.length > 0);
    }, [groups, searchValue, getOptionLabel, getOptionValue]);

    const selectedValue = Array.isArray(value) ? value : [];
    const closedLabel = selectedValue.length === 0
        ? placeholder
        : selectedValue.length === 1
            ? getOptionLabel(selectedValue[0])
            : `${selectedValue.length} selected`;

    const openSheet = () => {
        if (isDisabled) return;
        setDraftValue([...selectedValue]);
        setSearchValue('');
        setIsOpen(true);
    };

    const closeSheet = () => {
        setIsOpen(false);
    };

    const toggleOption = (option) => {
        if (isOptionDisabled?.(option)) return;

        const key = optionKey(option);
        setDraftValue((current) => current.some((item) => optionKey(item) === key)
            ? current.filter((item) => optionKey(item) !== key)
            : [...current, option]);
    };

    const applySelection = () => {
        onChange?.(draftValue, { action: 'set-value' });
        setIsOpen(false);
    };

    if (!isMobile) {
        return (
            <Select
                {...selectProps}
                inputId={inputId}
                name={name}
                isMulti
                options={options}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                isDisabled={isDisabled}
                getOptionValue={getOptionValue}
                getOptionLabel={getOptionLabel}
                isOptionDisabled={isOptionDisabled}
                formatOptionLabel={formatOptionLabel}
                className={className}
                classNamePrefix={classNamePrefix}
            />
        );
    }

    return (
        <>
            <button
                ref={triggerRef}
                id={triggerId}
                type="button"
                className={`form-select profile-responsive-multi-select__trigger ${className || ''}`}
                onClick={openSheet}
                disabled={isDisabled}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
            >
                <span className={selectedValue.length === 0 ? 'is-placeholder' : ''}>{closedLabel}</span>
            </button>

            <Modal
                show={isOpen}
                onHide={closeSheet}
                className="profile-multi-select-mobile-modal"
                dialogClassName="profile-multi-select-mobile-dialog"
                contentClassName="profile-multi-select-mobile-content"
                aria-labelledby={titleId}
                restoreFocus
            >
                <Modal.Header closeButton closeLabel={`Close ${label}`}>
                    <div className="profile-multi-select-mobile-handle" aria-hidden="true" />
                    <div className="profile-multi-select-mobile-heading">
                        <Modal.Title id={titleId}>{label}</Modal.Title>
                        <span className="profile-multi-select-mobile-count" aria-live="polite">
                            {draftValue.length} selected
                        </span>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <Form.Control
                        type="search"
                        className="profile-multi-select-mobile-search"
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        placeholder={searchPlaceholder || `Search ${label.toLowerCase()}...`}
                        aria-label={`Search ${label}`}
                    />

                    <div className="profile-multi-select-mobile-options" role="listbox" aria-multiselectable="true">
                        {filteredGroups.map((group, groupIndex) => (
                            <div className="profile-multi-select-mobile-group" key={group.label || groupIndex}>
                                {group.label && (
                                    <div className="profile-multi-select-mobile-group__label">{group.label}</div>
                                )}

                                {group.options.map((option) => {
                                    const key = optionKey(option);
                                    const selected = draftKeys.has(key);
                                    const disabled = Boolean(isOptionDisabled?.(option));

                                    return (
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={selected}
                                            className={`profile-multi-select-mobile-option${selected ? ' is-selected' : ''}`}
                                            key={key}
                                            onClick={() => toggleOption(option)}
                                            disabled={disabled}
                                        >
                                            <span className="profile-multi-select-mobile-option__check" aria-hidden="true">
                                                {selected ? '\u2713' : ''}
                                            </span>
                                            <span className="profile-multi-select-mobile-option__label">
                                                {mobileFormatOptionLabel
                                                    ? mobileFormatOptionLabel(option, { context: 'menu', inputValue: searchValue, selectValue: draftValue })
                                                    : formatOptionLabel
                                                    ? formatOptionLabel(option, { context: 'menu', inputValue: searchValue, selectValue: draftValue })
                                                    : getOptionLabel(option)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ))}

                        {filteredGroups.length === 0 && (
                            <div className="profile-multi-select-mobile-empty">No options found</div>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button type="button" variant="outline-secondary" onClick={closeSheet}>Cancel</Button>
                    <Button type="button" variant="primary" onClick={applySelection}>Apply</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ResponsiveMultiSelect;
